import { Coffee, HeartHandshake, ShieldCheck, X } from "lucide-react";

/**
 * Soft "waiting room" for clients who join before the counsellor has started the session.
 * Renders while the counsellor is still on their way — turns dead air into a calm beat.
 *
 * Props:
 *   counsellorName: string
 *   startedAt: ISO string | null — flip to non-null to auto-transition to the video
 *   onCancel: () => void — client hit the "Leave waiting room" button
 *   secondsWaiting: number — how long the client has been waiting (for the timer label)
 */
export default function WaitingRoom({ counsellorName, onCancel, secondsWaiting = 0 }) {
  const mins = Math.floor(secondsWaiting / 60);
  const secs = secondsWaiting % 60;
  const timerText = mins > 0 ? `${mins}m ${String(secs).padStart(2, "0")}s` : `${secs}s`;
  const gentleHint = secondsWaiting > 300
    ? "Still waiting? Feel free to send them a chat message — sometimes life runs a few minutes late."
    : "Take a slow breath. In through your nose for four, out through your mouth for six.";

  return (
    <div
      className="relative overflow-hidden rounded-3xl border border-[#3D405B]/10 bg-gradient-to-br from-[#F9F6F0] via-[#F4E8D7]/60 to-[#E8DDCF]"
      data-testid="waiting-room"
      style={{ minHeight: 520 }}
    >
      {/* Soft blob backdrop */}
      <div aria-hidden className="pointer-events-none absolute -top-24 -right-16 w-96 h-96 rounded-full bg-secondary/25 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-24 -left-12 w-80 h-80 rounded-full bg-primary/20 blur-3xl" />

      <div className="relative p-8 sm:p-12 flex flex-col items-center text-center h-full">
        {/* Breathing halo */}
        <div className="relative flex items-center justify-center w-40 h-40 mb-6">
          <span aria-hidden className="absolute inset-0 rounded-full bg-secondary/25 animate-ping" style={{ animationDuration: "3.2s" }} />
          <span aria-hidden className="absolute inset-4 rounded-full bg-secondary/40 animate-pulse" style={{ animationDuration: "2.5s" }} />
          <div className="relative w-24 h-24 rounded-full bg-white shadow-xl flex items-center justify-center">
            <HeartHandshake size={40} className="text-secondary" />
          </div>
        </div>

        <div className="pill bg-secondary/15 text-secondary mb-3" data-testid="waiting-room-timer">
          Waiting · {timerText}
        </div>
        <h2 className="text-2xl sm:text-3xl font-extrabold text-[#3D405B] tracking-tight max-w-xl">
          You&apos;re in the room. {counsellorName || "Your counsellor"} has been notified.
        </h2>
        <p className="text-[#5F627D] mt-3 max-w-md leading-relaxed">
          They&apos;ll join in a moment. This space is private and confidential — nothing you do here is recorded until the session starts.
        </p>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl w-full">
          <div className="rounded-2xl bg-white/70 backdrop-blur border border-white/50 p-4 text-left">
            <ShieldCheck size={18} className="text-primary mb-2" />
            <div className="text-xs font-extrabold uppercase tracking-wider text-[#3D405B]">Fully private</div>
            <div className="text-xs text-[#7A7D96] mt-1">End-to-end encrypted. POPIA-compliant.</div>
          </div>
          <div className="rounded-2xl bg-white/70 backdrop-blur border border-white/50 p-4 text-left">
            <Coffee size={18} className="text-primary mb-2" />
            <div className="text-xs font-extrabold uppercase tracking-wider text-[#3D405B]">Take a breath</div>
            <div className="text-xs text-[#7A7D96] mt-1">{gentleHint}</div>
          </div>
          <div className="rounded-2xl bg-white/70 backdrop-blur border border-white/50 p-4 text-left">
            <HeartHandshake size={18} className="text-primary mb-2" />
            <div className="text-xs font-extrabold uppercase tracking-wider text-[#3D405B]">You&apos;re not alone</div>
            <div className="text-xs text-[#7A7D96] mt-1">Whatever brought you here, this space holds it.</div>
          </div>
        </div>

        <button
          type="button"
          onClick={onCancel}
          className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#3D405B]/15 bg-white/80 text-[#3D405B] font-bold hover:bg-white transition-all text-sm"
          data-testid="waiting-room-cancel"
        >
          <X size={14} /> Leave the waiting room
        </button>
      </div>
    </div>
  );
}
